<template>
  <div class="project-one-lead-gen-content store-locator">
    <div class="project-one-lead-gen-content__header">
      <h2 class="heading heading--lg mb-1" qaid="dream-bike-header" v-html="bp('buildDreamBike')" />
      <p class="text-sm" qaid="send-order-text" v-html="bp('orderSend')" />
    </div>

    <div class="project-one-lead-gen-content__bike-info">
      <h3 class="heading heading--md" qaid="model-name-header" v-html="bikeData.modelName" />

      <div class="flex items-center justify-between mt-2">
        <p class="text-sm" qaid="base-price-header" v-html="bp('orderBasePrice')" />
        <span v-if="isHidingPrices" qaid="base-price" class="text-sm" v-html="bp('priceVaries')" />
        <span
          v-else-if="b2b && !consumerFriendlyMode"
          class="text-blue text-sm"
          qaid="base-price"
          v-html="bikeData.defaultDealerCost"
        />
        <span v-else class="text-blue text-sm" qaid="base-price" v-html="bikeData.defaultAdvertised" />
      </div>

      <div class="flex items-center justify-between mt-2">
        <p class="text-sm" qaid="estimated-price-header" v-html="bp('orderEstimatedPrice')" />
        <span v-if="isHidingPrices" qaid="estimated-price" class="text-sm" v-html="bp('priceVaries')" />
        <span
          v-else-if="b2b && !consumerFriendlyMode"
          class="text-blue text-sm"
          qaid="estimated-price"
          v-html="bikeData.calculatedDealerCost"
        />
        <span v-else class="text-blue text-sm" qaid="estimated-price" v-html="bikeData.calculatedAdvertised" />
      </div>

      <p class="text-xs text-gray-60" qaid="price-exclusion-text" v-html="bp('orderEstimatedPriceExcluding')" />
    </div>

    <h3 class="heading heading--md mb-3" qaid="price-exclusion-text" v-html="bp('orderDetails')" />
    <div class="md:max-w-xs">
      <form @submit.prevent>
        <form-group
          v-model="customerName"
          v-validate="'required'"
          type="text"
          name="name"
          :label="bp('requestReturnLabelName')"
          :required="true"
          :disabled="true"
          qaid="lead-gen-name-input"
          :show-error-messages="true"
          :error="errors.first('name')"
        />

        <form-group
          v-model="customerEmail"
          v-validate="'required|email'"
          type="email"
          name="email"
          :disabled="true"
          :label="bp('forgottenPwdEmail')"
          :required="true"
          qaid="lead-gen-email-input"
          :show-error-messages="true"
          :error="errors.first('email')"
        />

        <form-group
          v-model="customerPhoneNumber"
          v-validate="'required'"
          :required="true"
          allowed-characters-regex="[\d-+. ()]"
          type="tel"
          :maxlength="20"
          name="customerPhoneNumber"
          :label="bp('contactUsPhone')"
          qaid="lead-gen-phone-input"
        />

        <form-group
          type="radio"
          name="customerContactMethod"
          :multi-items="preferredContactMethods"
          :label="bp('orderContactMethod')"
          :required="true"
          qaid="lead-gen-contact-method"
          :value="selectedContactMethod.value"
          @change="handleContactMethodChange"
        />
      </form>
    </div>
    <div v-if="retailerLocation.id">
      <h3 class="heading heading--md mb-3" qaid="selected-retailer-header" v-html="bp('selectedRetailer')" />
      <locator-store-card :base-properties="baseProperties" :value="retailerLocation" />
    </div>
    <div v-else>
      <form @submit.prevent>
        <h3 class="heading heading--md mb-3" qaid="register-retailer-header" v-html="bp('registerRetailerTitle')" />
        <div class="form-group">
          <label class="form-label" qaid="enter-city-label">
            <span v-html="bp('orderLocation')" />
            <span qaid="enter-city-label-asterick" class="text-red ml-25">*</span>
          </label>

          <div class="form-group--inline is-required">
            <input
              id="search-location"
              v-model="textSearchQuery"
              type="text"
              class="form-control md:max-w-xs"
              qaid="lead-gen-retailer-location-search-input"
              name="search-location"
              maxlength="150"
              required="required"
              @keypress.enter="search"
            />
            <trek-button
              primary
              form-inline
              class="ml-1"
              qaid="lead-gen-location-search-button"
              @click.prevent="search"
            >
              <span qaid="lead-gen-location-search-button-text" v-html="bp('registerRetailerSearch')" />
            </trek-button>
          </div>
        </div>
      </form>
      <div class="locator-container">
        <locator-store-list
          v-model="stores"
          :base-properties="baseProperties"
          :display-empty-message="hasSearched"
          :has-img-for-no-records="false"
          :is-loading="isLoading"
          :is-scroll-list="false"
          qaid="store-locator__store-list--mobile"
          @store-clicked="setRetailerLocation"
        />
        <pagination
          v-if="totalPages > 1 && stores.length"
          class="md:px-2"
          container-class="justify-between"
          :display-pages="false"
          :page-number="pagination.page"
          :page-count="totalPages"
          @page-change="pageChange"
          @prev-page="prevPage"
          @next-page="nextPage"
        >
          <template v-slot:previous>
            <span class="text-md ml-1" v-html="bp('paginationPrevious')" />
          </template>
          <template v-slot:next>
            <span class="text-md mr-1" v-html="bp('paginationNext')" />
          </template>
        </pagination>
      </div>
    </div>
    <div class="buttons mt-3">
      <trek-button primary qaid="lead-gen-control__submit-btn" :disabled="!isSubmittable" @click="submit">
        <span v-html="bp('contactUsSubmit')" />
      </trek-button>
      <trek-button
        v-if="retailerLocation.id"
        qaid="lead-gen-select-new-retailer-button"
        secondary
        @click="clearRetailer"
      >
        <span qaid="lead-gen-select-new-retailer-button-text" v-html="bp('clearRetailer')" />
      </trek-button>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions, mapMutations} from 'vuex';
import FormGroup from '@/components/FormGroup';
import TrekButton from '@/components/TrekButton';
import {LocatorApi} from '@/components/locator/mixins/locator-api';
import Pagination from '@/components/Pagination';
import LocatorStoreList from '@/components/locator/components/LocatorStoreList';
import {UserApi} from '@/api/user';
import LocatorStoreCard from '@/components/locator/components/LocatorStoreCard';

export default {
  name: 'ProjectOneLeadGenControl',

  components: {FormGroup, TrekButton, Pagination, LocatorStoreList, LocatorStoreCard},

  mixins: [LocatorApi],

  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      customerEmail: '',
      customerName: '',
      customerPhoneNumber: '',
      retailerLocation: {},
      searchInputError: '',
      preferredContactMethods: [],
      textSearchQuery: '',
      stores: [],
      pagination: {},
      isProjectOneRetailerSearch: true,
      isLoading: false,
      hasSearched: false,
    };
  },

  computed: {
    selectedContactMethod() {
      // Will find email as default (checked: true) in preferredContactMethods
      return this.preferredContactMethods.find(({checked}) => checked === true);
    },

    totalPages() {
      if (!this.pagination.pageSize) {
        return 1;
      }
      return Math.ceil(this.pagination.totalResults / this.pagination.pageSize);
    },
    isSubmittable() {
      return !!(
        this.customerEmail &&
        this.customerPhoneNumber &&
        this.selectedContactMethod.value &&
        this.retailerLocation.id
      );
    },

    ...mapState('projectOne', ['bikeData', 'isHidingPrices']),
    ...mapState('backend', ['b2b', 'consumerFriendlyMode']),
  },

  created() {
    this.preferredContactMethods = [
      // Starting off with email as true as the default contact method since the field is required
      {value: 'email', displayName: this.bp('contactUsEmail'), checked: true},
      {value: 'phone', displayName: this.bp('contactUsPhone'), checked: false},
    ];
    UserApi.getCustomerData().then((response) => {
      if (response?.data?.data) {
        this.customerName = response.data.data.name;
        this.customerEmail = response.data.data.uid;
      }
    });
    this.fetchModule({
      buildId: this.$route.params.buildId,
      module: 'summary',
    });
  },

  methods: {
    ...mapActions('projectOne', ['submitToRetailer', 'fetchModule']),
    ...mapMutations('projectOne', ['setIsSaveModalVisible', 'setOnSaveCallbackFunction']),
    submit() {
      this.setOnSaveCallbackFunction(this.handleSubmission);
      if (this.bikeData?.code && this.bikeData?.description) {
        this.handleSubmission();
      } else {
        this.setIsSaveModalVisible(true);
      }
    },
    handleSubmission() {
      let data = {
        dealerId: this.retailerLocation.id,
        contactMethod: this.selectedContactMethod.value,
        name: this.customerName,
        email: this.customerEmail,
        phone: this.customerPhoneNumber,
      };
      this.submitToRetailer(data);
    },
    setRetailerLocation(locationId) {
      const storeIndex = this.stores.findIndex((store) => store.id === locationId);
      this.retailerLocation = this.stores[storeIndex];
    },
    clearRetailer() {
      this.retailerLocation = {};
    },
    handleContactMethodChange(event) {
      /* Loops through contact methods to find the value that matches
          the event and sets all others in the array to false
      */
      this.preferredContactMethods.filter((contactMethod) => {
        if (event && event.target && contactMethod.value === event.target.value) {
          contactMethod.checked = event.target.checked;
        } else {
          contactMethod.checked = false;
        }
      });
    },
    search() {
      this.isLoading = true;
      this.loadData();
      if (!this.hasSearched) {
        this.hasSearched = true;
      }
    },

    pageChange(page) {
      this.loadData(page);
    },

    prevPage() {
      if (this.pagination.page > 1) {
        this.pageChange(this.pagination.page - 1);
      }
    },

    nextPage() {
      if (this.pagination.page < this.totalPages) {
        this.pageChange(this.pagination.page + 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.project-one-lead-gen-content {
  margin: 2rem 1rem;

  &__header {
    margin-bottom: 3rem;
  }

  &__bike-info {
    margin-bottom: 3rem;
  }
}

@screen lg {
  .project-one-lead-gen-content {
    margin: 4rem 2rem 2rem;
  }
}
</style>
